import { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../../store/types';
import CheckmarkTextInput from '../inputs/CheckmarkTextInput';
import { ButtonTypes } from '../../services/exports/Interfaces';
import { COUPON_TYPES } from '../../services/exports/Constants';
import MultiPromoCodeModal from '../../modals/MultiPromoCodeModal';
import useBasket from '../../hooks/basket/useBasket';
import MinimumValueErrorLabel from '../labels/MinimumValueErrorLabel';
import useAppMethods from '../../hooks/utility/useAppMethods';
import useAuth from '../../hooks/global/useAuth';
import CashbackToggle from './CashbackToggle';
import CustomButton from '../buttons/CustomButton';
import Highlighter from 'react-highlight-words';
import collect from 'collect.js';
import classnames from 'classnames';
import useMobileApp from '../../hooks/mobile-app/useMobileApp';
import { Modals } from '../../modules/mobile/exports/Enums';
import useModalRouter from '../../modules/mobile/common/hooks/useModalRouter';
import usePromoCode from '../../hooks/order/usePromoCode';

interface Props {
  showCashbackToggle?: boolean;
  className?: string;
  titleClassName?: string;
}

const PromoCodeInput = forwardRef((props: Props, ref) => {
  useImperativeHandle(ref, () => ({ setPromoCode, removePromoCode }));

  const { t, i18n } = useTranslation(null, { keyPrefix: 'Components:Checkout:PromoCodeInput' });

  const { showCashbackToggle, className, titleClassName } = props;

  const { cached_order } = useSelector((state: StoreInterface) => state.order);
  const { isMobileApp } = useMobileApp();
  const { formatCurrency, toggleLoginModal } = useAppMethods();
  const { openModal } = useModalRouter();
  const { isLoggedIn } = useAuth();
  const { subtotal } = useBasket();
  const {
    promoCodeInput,
    appliedPromoCode,
    setPromoCode,
    setPromoCodeInput,
    removePromoCode,
    isLoading,
    applyPromoCode,
  } = usePromoCode();

  const isApplied = !!appliedPromoCode;
  const [showMultiPromoCodeModal, setShowMultiPromoCodeModal] = useState(false);

  const login = (): void => {
    if (isMobileApp) {
      return openModal({ id: Modals.Login });
    }
    toggleLoginModal({
      openModal: true,
      onCloseRedirect: () => null,
    });
  };

  const _applyPromoCode = (): void => {
    if (!isLoggedIn) {
      return login();
    }
    applyPromoCode();
  };

  const highlightedDiscountLabel = useMemo<string[]>(
    () =>
      collect()
        .when(cached_order?.coupon_type === COUPON_TYPES.CASHBACK, (collection) =>
          collection.push(t('discount_types.cashback')),
        )
        .when(cached_order?.coupon_type === COUPON_TYPES.PROMO_CODE, (collection) =>
          collection.push(t('discount_types.promo_code')),
        )
        .toArray(),
    [cached_order?.coupon_type, i18n.language],
  );

  const toggleMultiPromoCodeModal = () => {
    setShowMultiPromoCodeModal(!showMultiPromoCodeModal);
  };

  const renderMultiPromoCodeModal = useMemo(() => {
    return <MultiPromoCodeModal openModal={showMultiPromoCodeModal} toggleModal={toggleMultiPromoCodeModal} />;
  }, [showMultiPromoCodeModal]);

  const hasSubtotalExceededThreshold = (): boolean => {
    if (!cached_order?.coupon_details?.components) {
      return false;
    }

    const component = cached_order.coupon_details.components.find((component) => !component.is_used);

    return component && subtotal >= component.usage_threshold;
  };

  const renderMinimumValueError = useMemo(() => {
    if (
      !cached_order?.coupon_details ||
      hasSubtotalExceededThreshold() ||
      !cached_order?.coupon_details?.components ||
      cached_order?.coupon_details?.components?.length === 0
    ) {
      return null;
    }

    return (
      <MinimumValueErrorLabel
        description={t('feedback.usage_threshold')}
        value={formatCurrency(cached_order?.coupon_details?.components[0]?.usage_threshold)}
        className="mt-xs"
        type="Notice"
      />
    );
  }, [cached_order?.coupon_details, subtotal]);
  const isApplyDisabled = promoCodeInput?.length === 0;

  return (
    <div className={className}>
      <label htmlFor="promo-code" className={classnames('text-sm font-medium', titleClassName)}>
        {/* @ts-expect-error Weird ts error */}
        <Highlighter
          highlightTag="span"
          highlightClassName="font-medium underline text-black"
          autoEscape={true}
          searchWords={highlightedDiscountLabel}
          textToHighlight={t('input.label', {
            cashbackLabel: t('discount_types.cashback'),
            promoCodeLabel: t('discount_types.promo_code'),
          })}
        />
      </label>
      {showCashbackToggle && <CashbackToggle subtotal={subtotal} className="mt-sm" />}
      <div className="flex gap-xs mt-xs">
        <CheckmarkTextInput
          type="uppercase"
          placeholder={t('input.placeholder')}
          value={promoCodeInput}
          onChange={setPromoCodeInput}
          disabled={isApplied}
          id="promo-code"
          classes={{
            container: 'w-full',
            inputWrapper: classnames('lg:py-xs'),
          }}
        />
        <CustomButton
          title={isApplied ? t('buttons.remove') : t('buttons.apply')}
          buttonType={isApplied ? ButtonTypes.danger : ButtonTypes.primary}
          onClick={isApplied ? removePromoCode : _applyPromoCode}
          loading={isLoading}
          disabled={isApplyDisabled}
          className={classnames('h-[36px] rounded-md !w-fit border-xs border-mobile-secondary shadow-none', {
            '!bg-white': !isApplied,
          })}
          titleClass={classnames('font-normal', { '!text-gray-700': isApplyDisabled })}
        />
      </div>
      {renderMinimumValueError}
      {renderMultiPromoCodeModal}
    </div>
  );
});

export default PromoCodeInput;
